import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { formatUsd } from 'shared';
import { Button } from '@/components/button.js';
import { ProductDescription } from '@/components/product-description.js';
import { SurchargeToggle } from '@/components/surcharge-toggle.js';
import { TermChipRow } from '@/flows/outreach/components/term-chip-row.js';
import { useProductCard } from '@/lib/use-product-card.js';

interface OutreachProductCardProps {
  /** `quote_item_id`s of this product's tiers, ascending by term_months. */
  quoteItemIds: string[];
  expanded: boolean;
  onToggleExpand: () => void;
  /** Fired with the new cart index — the flow then asks how to pay for this plan. */
  onSelected: (cartIndex: number) => void;
  /**
   * False once the conversation has moved past this set of plans. Cards stay in the thread
   * as history, and an interactive one would let a customer mid-financing change a term or
   * add-on out from under the payment step they're on — so they collapse to a read-only row.
   */
  selectable: boolean;
}

export function OutreachProductCard({
  quoteItemIds,
  expanded,
  onToggleExpand,
  onSelected,
  selectable,
}: OutreachProductCardProps) {
  const model = useProductCard(quoteItemIds);
  // The chat pins a card's quote-item ids into an immutable message, so a re-rate
  // retires them. Drop the card rather than render a broken one.
  if (model === null) return null;

  const {
    termTiers,
    currentTier,
    productName,
    productDescription,
    isInCart,
    currentSubtotal,
    lowestSubtotal,
    termMonthsLabel,
    coverageMilesLabel,
    deductibleLabel,
    getSurchargeSelected,
    toggleSurcharge,
    handleTermChange,
    handleSelectPlan,
    handleRemove,
  } = model;

  const termRange =
    termTiers.length > 1
      ? `${termTiers[0]!.term_months}–${termTiers[termTiers.length - 1]!.term_months} mo`
      : termMonthsLabel;
  const summarySubtext = !isInCart ? termRange : termMonthsLabel;
  const headlineSubtotal = isInCart ? currentSubtotal : lowestSubtotal;

  const summary = (
    <>
      {isInCart ? (
        <span className="sm-center sm-check-disc" aria-hidden="true">
          <CheckIcon size={12} strokeWidth={3} />
        </span>
      ) : null}
      <span className="sm-stack sm-pair sm-chat-product-name-block">
        <span className="sm-type-body-strong sm-chat-product-name">{productName}</span>
        <span className="sm-type-caption sm-tabular">{summarySubtext}</span>
      </span>
      {headlineSubtotal !== null ? (
        <span className="sm-stack sm-tabular sm-pair-end">
          {!isInCart ? <span className="sm-type-eyebrow">From</span> : null}
          <span className="sm-type-heading sm-amount">{formatUsd(headlineSubtotal)}</span>
        </span>
      ) : null}
    </>
  );

  if (!selectable) {
    return (
      <div
        className="sm-panel sm-animate-in sm-chat-tail sm-chat-settled sm-chat-product-card"
        data-selected={isInCart ? 'true' : 'false'}
      >
        {summary}
      </div>
    );
  }

  return (
    <div
      className="sm-panel sm-stack sm-animate-in sm-chat-tail sm-chat-product-card"
      data-selected={isInCart ? 'true' : 'false'}
    >
      <button
        type="button"
        className="sm-summary-row sm-chat-product-summary"
        aria-expanded={expanded}
        onClick={onToggleExpand}
      >
        {summary}
        <ChevronDownIcon className="sm-chevron" size={18} aria-hidden="true" />
      </button>

      {expanded ? (
        <div className="sm-stack sm-chat-product-body">
          <ProductDescription doc={productDescription} />

          {termTiers.length > 1 ? (
            <div className="sm-stack sm-chat-product-term-group">
              <span className="sm-type-eyebrow">Choose a term</span>
              <TermChipRow
                options={termTiers.map((t) => t.term_months)}
                selected={currentTier.term_months}
                onSelect={handleTermChange}
                ariaLabel="Term length"
              />
            </div>
          ) : null}

          <div className="sm-chat-product-specs">
            <div className="sm-stack sm-pair sm-chat-product-spec">
              <span className="sm-type-eyebrow">Term</span>
              <span className="sm-type-body-strong sm-tabular">{termMonthsLabel}</span>
            </div>
            <div className="sm-stack sm-pair sm-chat-product-spec">
              <span className="sm-type-eyebrow">Coverage</span>
              <span className="sm-type-body-strong sm-tabular">{coverageMilesLabel}</span>
            </div>
            <div className="sm-stack sm-pair sm-chat-product-spec">
              <span className="sm-type-eyebrow">Deductible</span>
              <span className="sm-type-body-strong sm-tabular">{deductibleLabel}</span>
            </div>
          </div>

          {currentTier.surcharges.length > 0 ? (
            <div className="sm-stack">
              <span className="sm-type-eyebrow sm-chat-product-addons-label">Add-ons</span>
              {currentTier.surcharges.map((s) => (
                <SurchargeToggle
                  key={s.code}
                  description={s.description}
                  basePrice={s.base_price}
                  optional={s.optional}
                  selected={s.optional ? getSurchargeSelected(s.code) : true}
                  onToggle={() => toggleSurcharge(s.code)}
                />
              ))}
            </div>
          ) : null}

          <div className="sm-type-caption sm-tabular sm-chat-product-total">
            {formatUsd(currentSubtotal)} subtotal
          </div>

          <Button
            onClick={() => {
              if (isInCart) {
                handleRemove();
                return;
              }
              const cartIndex = handleSelectPlan();
              if (cartIndex !== null) onSelected(cartIndex);
            }}
            variant={isInCart ? 'secondary' : 'primary'}
            endIcon={null}
          >
            {isInCart ? 'Remove selection' : 'Select plan'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
